import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useCreateSniperGroup } from '../../api/useSniper';

/**
 * Create dialog for a Sniper group — mirrors the opener's `CreateGroupDialog`
 * (compact `xs` dialog, small title, a single Name field, Cancel / Create
 * actions with an inline spinner while saving). Behaviour is unchanged from the
 * previous inline "New group name / ADD GROUP" bar: it posts the SAME
 * `{ name, sport: 'soccer' }` body via `useCreateSniperGroup` and toasts on
 * failure. Sniper groups are soccer-only today, so `sport` stays fixed.
 */
export function SniperCreateGroupDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const createGroup = useCreateSniperGroup();
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) setName('');
  }, [open]);

  const canSave = !!name.trim() && !createGroup.isLoading;

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createGroup.mutate(
      { name: trimmed, sport: 'soccer' },
      {
        onSuccess: () => onClose(),
        onError: () => {
          toast.error('Failed to create group.');
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>New Sniper group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSave) handleSave();
            }}
            fullWidth
            autoFocus
          />
          <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
            A group holds one or more matches; add matches after it is created.
            Start / Stop and Panic are per-group.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          size="small"
        >
          {createGroup.isLoading ? <CircularProgress size={16} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
